import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type {
  RunLogLinesQuery,
  RunLogLinesQueryVariables,
} from '../../generated/graphql';
import type {
  ApolloError,
  ApolloQueryResult,
} from '../../state/graphql/apolloWithAbort';
import {RUN_LOG_LINES_QUERY} from './runLogLines.query';

export const PAGE_SIZE_IMPROVED_PAGINATION_MAX = 2_000;
export const PAGE_SIZE_MAX = 10_000;
export {RUN_LOG_LINES_QUERY};

export type LogLines = OmitTypename<
  NonNullable<
    NonNullable<NonNullable<RunLogLinesQuery['project']>['run']>['logLines']
  >
>;
type OmitTypename<T> =
  T extends Array<infer U>
    ? Array<OmitTypename<U>>
    : T extends object
      ? {[K in keyof T as Exclude<K, '__typename'>]: OmitTypename<T[K]>}
      : T;

export interface UseRunLogQueryParams {
  entityName: string;
  pollInterval: number;
  projectName: string;
  runName: string;
  skip?: boolean;
  useImprovedPagination?: boolean;
}

type UseRetryInitialLoadArgs = {
  isBlocked: boolean;
  refetch: (
    variables?: RunLogLinesQueryVariables
  ) => Promise<ApolloQueryResult<RunLogLinesQuery>>;
  setIsRetrying: Dispatch<SetStateAction<boolean>>;
};

export function useRetryInitialLoad({
  isBlocked,
  refetch,
  setIsRetrying,
}: UseRetryInitialLoadArgs): [() => void, ApolloError | undefined] {
  const [retryError, setRetryError] = useState<ApolloError | undefined>();

  const retryInitialLoad = useCallback(() => {
    if (isBlocked) {
      return;
    }

    setIsRetrying(true);

    return refetch()
      .then(_ => setRetryError(undefined))
      .catch(setRetryError)
      .finally(() => setIsRetrying(false));
  }, [isBlocked, refetch, setIsRetrying]);

  return [retryInitialLoad, retryError];
}

type UsePollingArgs = {
  canPoll: boolean;
  fetchNext: () => void;
  hasNextPage: boolean;
  pollInterval: number;
};

type UsePollingResult = {
  isPolling: boolean;
  togglePolling: (isPolling: boolean) => void;
};

export function usePolling({
  canPoll,
  fetchNext,
  hasNextPage,
  pollInterval,
}: UsePollingArgs): UsePollingResult {
  // Start with polling disabled; enable it when canPoll first becomes true.
  // This avoids a race condition where initial state is true but canPoll
  // is false during data loading.
  const hasInitializedPolling = useRef(false);
  const [isPolling, setIsPolling] = useState(false);

  const togglePolling = useCallback(
    (shouldPoll: boolean) => {
      if (shouldPoll && canPoll) {
        setIsPolling(true);
      } else {
        setIsPolling(false);
      }
    },
    [canPoll]
  );

  useEffect(() => {
    if (canPoll && !hasInitializedPolling.current) {
      hasInitializedPolling.current = true;
      setIsPolling(true);
    }

    const isDataExhausted = !canPoll && !hasNextPage;
    if (isDataExhausted && hasInitializedPolling.current && isPolling) {
      setIsPolling(false);
    }
  }, [canPoll, hasNextPage, isPolling]);

  const fetchNextRef = useRef(fetchNext);
  // oxlint-disable-next-line react/refs
  fetchNextRef.current = fetchNext;

  const pollingIntervalId = useRef<NodeJS.Timeout | void>(undefined);
  useEffect(() => {
    const clearIntervalId = () => {
      if (pollingIntervalId.current) {
        clearInterval(pollingIntervalId.current);
        pollingIntervalId.current = undefined;
      }
    };

    clearIntervalId();

    const canFetchMore = canPoll || hasNextPage;
    if (!isPolling || !canFetchMore) {
      return;
    }

    pollingIntervalId.current = setInterval(
      () => fetchNextRef.current(),
      pollInterval
    );

    return clearIntervalId;
  }, [canPoll, hasNextPage, isPolling, pollInterval]);

  return {isPolling, togglePolling};
}
